import type { PersistenceService } from '@/services/persistence';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useCameraStore } from '@/store/cameraStore';
import { repairDocument, validateDocument } from '@/core/scene/sceneValidation';
import type { WorkspaceDocument, WorkspaceId } from '@/types';
import { asWorkspaceId, isConnectionType, isContainer } from '@/types';
import { buildShowcaseWorkspace } from '@/features/showcase/showcaseWorkspace';
import { validateDocument as validate } from '@/core/scene/sceneValidation';
import { createId } from '@/utils/id';

/**
 * Persisted documents are untrusted: each is validated; warning-level drift is
 * repaired, and a document with errors is dropped (logged) rather than loaded.
 */
/** Fills fields added after a document was saved so older snapshots keep loading. */
const upgrade = (doc: WorkspaceDocument): WorkspaceDocument => {
  const connections = Object.fromEntries(
    Object.entries(doc.connections).map(([id, c]) => [
      id,
      isConnectionType(c.type) ? c : { ...c, type: 'linked-to' as const },
    ]),
  ) as WorkspaceDocument['connections'];
  const objects = Object.fromEntries(
    Object.entries(doc.objects).map(([id, o]) => [
      id,
      isContainer(o) && o.expandedSize === undefined ? { ...o, expandedSize: null } : o,
    ]),
  ) as WorkspaceDocument['objects'];
  return {
    ...doc,
    objects,
    connections,
    workspace: Array.isArray(doc.workspace.bookmarks) ? doc.workspace : { ...doc.workspace, bookmarks: [] },
  };
};

const sanitize = (
  workspaces: Readonly<Record<WorkspaceId, WorkspaceDocument>>,
): Record<WorkspaceId, WorkspaceDocument> => {
  const out: Record<WorkspaceId, WorkspaceDocument> = {};
  for (const [id, raw] of Object.entries(workspaces) as [WorkspaceId, WorkspaceDocument][]) {
    try {
      const doc = upgrade(raw);
      const result = validateDocument(doc);
      if (!result.ok) {
        console.warn(`[vectorloom] workspace "${id}" failed validation and was not loaded`, result.issues);
        continue;
      }
      out[id] = result.issues.length ? repairDocument(doc) : doc;
    } catch (error) {
      // Validation is defensive, but a snapshot from an unknown source must never stop the app booting.
      console.warn(`[vectorloom] workspace "${id}" could not be read and was not loaded`, error);
    }
  }
  return out;
};

/**
 * Wires the workspace store to persistence:
 *  - hydrates on start,
 *  - saves (debounced) on every document change,
 *  - commits the live camera into the workspace record on a coarse interval,
 *  - flushes on `pagehide`.
 * Returns a disposer so the binding can be torn down (tests, hot reload).
 */
export interface PersistenceOptions {
  readonly cameraCommitMs?: number;
  /** Load the showcase workspace when there is no saved snapshot. */
  readonly seedShowcase?: boolean;
}

export const bindPersistence = (service: PersistenceService, options: PersistenceOptions = {}): (() => void) => {
  const cameraCommitMs = options.cameraCommitMs ?? 1000;
  const snapshot = service.load();
  if (snapshot) {
    const workspaces = sanitize(snapshot.workspaces);
    const activeWorkspaceId = workspaces[snapshot.activeWorkspaceId]
      ? snapshot.activeWorkspaceId
      : (Object.keys(workspaces) as WorkspaceId[])[0];
    if (activeWorkspaceId) useWorkspaceStore.getState().hydrate({ workspaces, activeWorkspaceId });
  } else if (options.seedShowcase) {
    // First run: start with a real, editable example workspace rather than an empty field.
    const id = asWorkspaceId(createId('ws'));
    const showcase = buildShowcaseWorkspace(id);
    if (validate(showcase).ok)
      useWorkspaceStore.getState().hydrate({ workspaces: { [id]: showcase }, activeWorkspaceId: id });
  }
  const initialDoc = useWorkspaceStore.getState().workspaces[useWorkspaceStore.getState().activeWorkspaceId];
  if (initialDoc) useCameraStore.getState().setCamera(initialDoc.workspace.camera);

  const unsubWorkspace = useWorkspaceStore.subscribe((s, prev) => {
    if (s.workspaces === prev.workspaces && s.activeWorkspaceId === prev.activeWorkspaceId) return;
    service.save({ workspaces: s.workspaces, activeWorkspaceId: s.activeWorkspaceId });
    if (s.activeWorkspaceId !== prev.activeWorkspaceId) {
      const doc = s.workspaces[s.activeWorkspaceId];
      if (doc) useCameraStore.getState().setCamera(doc.workspace.camera);
    }
  });

  let cameraTimer: ReturnType<typeof setTimeout> | null = null;
  const unsubCamera = useCameraStore.subscribe((s, prev) => {
    if (s.camera === prev.camera) return;
    if (cameraTimer) clearTimeout(cameraTimer);
    cameraTimer = setTimeout(() => {
      cameraTimer = null;
      useWorkspaceStore.getState().commitCamera(useCameraStore.getState().camera);
    }, cameraCommitMs);
  });

  const onPageHide = () => {
    useWorkspaceStore.getState().commitCamera(useCameraStore.getState().camera);
    service.flush();
  };
  globalThis.addEventListener?.('pagehide', onPageHide);

  return () => {
    unsubWorkspace();
    unsubCamera();
    if (cameraTimer) clearTimeout(cameraTimer);
    globalThis.removeEventListener?.('pagehide', onPageHide);
    service.dispose();
  };
};
